/**
 * Refund tiers shown before booking and cancelling: a port of the old site's `refundPolicyPreview`
 * (LOGIC.md PAY-12), which mirrors `services/cancellationPolicyService.js` of the backend:
 *   lead time ≤ 2 days        → full refund until the pickup
 *   2 < lead time ≤ 4 days    → full refund until 48 h before, then refund minus the service fee
 *   more than 4 days          → full until 48 h, minus the service fee until 24 h, then 50 %
 * The pickup time is read as a UTC wall time, as the backend does (H16: do not «fix»).
 */
import { getT, ns } from '@/i18n';
import { formatDayTime } from '@/lib/format';
import type { RefundTier } from '../../domain';

const DAY = 86_400_000;

export function refundTiers(start: string, pickupTime: string, now: Date): RefundTier[] {
  const [y, m, d] = start.split('-').map(Number);
  // `vehiclePickupDateTime`: a missing time is 12:00, an unreadable hour or minute 0 (legacy and server alike).
  const time = pickupTime || '12:00';
  const [h, mi] = time.split(':').map(Number);
  const pickup = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, h || 0, mi || 0);
  const lead = (pickup - now.getTime()) / DAY;
  const t = getT(ns.labels);
  // Shown as the wall-clock day and time of the pickup.
  const at = (offsetDays: number) => {
    const wall = new Date(pickup - offsetDays * DAY);
    const local = new Date(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate());
    return formatDayTime(local, time, t.locale);
  };
  if (lead <= 2) return [{ when: t('refund.untilStart'), outcome: t('refund.full'), tone: 'lime' }];
  if (lead <= 4)
    return [
      { when: t('refund.until', { when: at(2) }), outcome: t('refund.full'), tone: 'lime' },
      { when: t('refund.after'), outcome: t('refund.minusFee'), tone: 'warn' },
    ];
  return [
    { when: t('refund.until', { when: at(2) }), outcome: t('refund.full'), tone: 'lime' },
    { when: t('refund.until', { when: at(1) }), outcome: t('refund.minusFee'), tone: 'mint' },
    { when: t('refund.lastDay'), outcome: t('refund.half'), tone: 'warn' },
  ];
}
